import { useEffect, useRef } from "react";
import { OutlineActions, OutlineLabel, OutlinePlus } from "./OutlinePartial";

const isFixedElement = (el) => {
    for (let node = el; node && node !== document.documentElement; node = node.parentNode) {
        const pos = window.getComputedStyle(node).position;
        if (pos === 'fixed' || pos === 'sticky') return true;
    }
    return false;
};

const checkSectionPosition = (section) => {
    let isTop = true;
    let isBottom = true;

    let prev = section.previousElementSibling;
    while (prev) {
        if (prev.tagName.toLowerCase() === 'section-wrapper') {
            isTop = false;
            break;
        }
        prev = prev.previousElementSibling;
    }

    let next = section.nextElementSibling;
    while (next) {
        if (next.tagName.toLowerCase() === 'section-wrapper') {
            isBottom = false;
            break;
        }
        next = next.nextElementSibling;
    }

    return { isTop, isBottom };
};

const applyOutlineStyle = (outlineEl, targetWrapperEl, isFixed) => {
    if (!outlineEl) return;
    if (!targetWrapperEl || targetWrapperEl.classList.contains('--hidden')) {
        outlineEl.style.display = 'none';
        return;
    }

    const targetEl = targetWrapperEl.firstElementChild || targetWrapperEl;
    const rect = targetEl.getBoundingClientRect();

    if (rect.width === 0 && rect.height === 0) {
        outlineEl.style.display = 'none';
        return;
    }

    if (isFixed) {
        outlineEl.style.position = 'fixed';
        outlineEl.style.top = `${rect.top}px`;
        outlineEl.style.left = `${rect.left}px`;
    } else {
        let scrollY = window.scrollY, scrollX = window.scrollX;
        for (let p = targetEl.parentNode; p && p !== document.documentElement; p = p.parentNode) {
            scrollY += p.scrollTop || 0;
            scrollX += p.scrollLeft || 0;
        }
        outlineEl.style.position = 'absolute';
        outlineEl.style.top = `${rect.top + scrollY}px`;
        outlineEl.style.left = `${rect.left + scrollX}px`;
    }

    Object.assign(outlineEl.style, {
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        display: 'block'
    });

    outlineEl.classList.toggle('__top', rect.top <= 0);
    outlineEl.classList.toggle('__bottom-inside', (rect.bottom + 50) > window.innerHeight);
};

function OutlineSection({ activeId, onClick, onHide, onRemove, onMoveUp, onMoveDown, onAdd, setSelectedElementId, isLimited }) {
    const state = useRef({
        activeEl: null, activeIsFixed: false,
        hoverEl: null, hoverIsFixed: false, pendingHoverEl: null, hoverTimer: null
    }).current;

    const refs = {
        hoverOut: useRef(null), activeOut: useRef(null),
        hoverTxt: useRef(null), activeTxt: useRef(null)
    };

    const resetHover = () => {
        clearTimeout(state.hoverTimer);
        state.pendingHoverEl = state.hoverEl = null;
        state.hoverIsFixed = false;
        if (refs.hoverOut.current) refs.hoverOut.current.style.display = 'none';
    };

    const activateSection = (section) => {
        if (!section) {
            state.activeEl = null;

            if (refs.activeOut.current) {
                refs.activeOut.current.style.display = 'none';
                refs.activeOut.current.classList.remove('__active', '__show-actions');
            }
            return;
        }

        state.activeEl = section;
        state.activeIsFixed = isFixedElement(section.firstElementChild || section);
        if (refs.activeTxt.current) refs.activeTxt.current.innerText = section.getAttribute('data-name') || 'Section';

        if (onClick) {
            const sectionId = section.getAttribute('section-id');
            onClick(sectionId);
        }

        resetHover();
        if (refs.activeOut.current) refs.activeOut.current.classList.add('__active', '__show-actions');
    };

    useEffect(() => {
        if (activeId) {
            const targetSection = document.querySelector(`section-wrapper[section-id="${activeId}"]`);
            //if (targetSection && targetSection !== state.activeEl) {
            if (targetSection) {
                activateSection(targetSection);
                setSelectedElementId(true);
                setTimeout(() => {
                    setSelectedElementId(false);
                }, 10);
            } else if (!targetSection) {
                // 🌟 THÊM DÒNG NÀY: Nếu có id mà DOM không có thì tắt outline
                activateSection(null);
            }
        } else {
            activateSection(null);
        }
    }, [activeId]);

    useEffect(() => {
        let frameId;
        const update = () => {
            // ==========================================
            // 🌟 1. KIỂM TRA ELEMENT CÒN TỒN TẠI KHÔNG?
            // ==========================================
            if (state.activeEl && !document.contains(state.activeEl)) {
                state.activeEl = null;
                if (refs.activeOut.current) {
                    refs.activeOut.current.style.display = 'none';
                    refs.activeOut.current.classList.remove('__active', '__show-actions');
                }
            }

            if (state.hoverEl && !document.contains(state.hoverEl)) {
                state.hoverEl = null;
                if (refs.hoverOut.current) refs.hoverOut.current.style.display = 'none';
            }

            // ==========================================
            // 2. VẼ LẠI GIAO DIỆN NHƯ BÌNH THƯỜNG
            // ==========================================
            applyOutlineStyle(refs.activeOut.current, state.activeEl, state.activeIsFixed);
            applyOutlineStyle(refs.hoverOut.current, state.hoverEl, state.hoverIsFixed);

            if (state.activeEl && refs.activeOut.current) {
                const { isTop, isBottom } = checkSectionPosition(state.activeEl);
                refs.activeOut.current.classList.toggle('--is-top', isTop);
                refs.activeOut.current.classList.toggle('--is-bottom', isBottom);
            }
        };
        const loop = () => { update(); frameId = requestAnimationFrame(loop); };

        loop();
        window.addEventListener('scroll', update, true);
        return () => { cancelAnimationFrame(frameId); window.removeEventListener('scroll', update, true); };
    }, []);

    useEffect(() => {
        const handleMouse = (e) => {
            const isClick = e.type === 'click';

            const outlineSection = e.target.closest('.smind-outline-section');
            const outlineBlock = e.target.closest('.smind-outline-block');

            if (outlineSection || outlineBlock) {
                if (isClick && e.target.closest('.smind-label') && outlineSection) {
                    const targetEl = state.hoverEl || state.activeEl;
                    if (targetEl) {
                        activateSection(targetEl);
                    }
                }
                return;
            }

            // ==========================================
            // 🌟 SỬA Ở ĐÂY: Đổi [smind-block-id] thành [smind-attributes]
            // Để Section nhường chỗ cho CẢ Block và Element
            // ==========================================
            if (e.target.closest('[smind-attributes]')) {
                if (isClick) {
                    activateSection(null);
                } else {
                    if (state.pendingHoverEl !== 'CHILD_HOVER') {
                        clearTimeout(state.hoverTimer);
                        state.pendingHoverEl = 'CHILD_HOVER';
                        state.hoverTimer = setTimeout(() => {
                            state.hoverEl = null;
                            if (refs.hoverOut.current) refs.hoverOut.current.style.display = 'none';
                        }, 200);
                    }
                }
                return; // Ngắt luồng, không cho Section chạy bật viền
            }

            const section = e.target.closest('section-wrapper');

            if (isClick) {
                activateSection(section);
                return;
            }

            if (refs.activeOut.current) {
                refs.activeOut.current.classList.toggle('__show-actions', section && section === state.activeEl);
            }

            if (section === state.pendingHoverEl) return;
            clearTimeout(state.hoverTimer);
            state.pendingHoverEl = section;

            if (section && section !== state.activeEl) {
                state.hoverTimer = setTimeout(() => {
                    state.hoverEl = section;
                    state.hoverIsFixed = isFixedElement(section.firstElementChild || section);
                    if (refs.hoverTxt.current) refs.hoverTxt.current.innerText = section.getAttribute('data-name') || 'Section';
                }, 200);
            } else {
                resetHover();
            }
        };

        // 🌟 1. THÊM HÀM NÀY: Xử lý khi chuột bay hẳn ra ngoài Iframe/Preview
        const handleMouseLeaveDocument = () => {
            clearTimeout(state.hoverTimer);
            state.pendingHoverEl = 'OUTSIDE';
            resetHover(); // Lập tức tắt viền hover
        };

        const handleForceActive = (e) => {
            const sectionId = e.detail;
            if (!sectionId) return;

            // Tìm thẻ section thật trong DOM dựa vào section-id
            const targetSection = document.querySelector(`section-wrapper[section-id="${sectionId}"]`);

            if (targetSection) {
                activateSection(targetSection); // Gọi hàm active y như click chuột!
            }
        };

        const handleForceDeactivate = () => {
            activateSection(null);
        }

        document.addEventListener('mouseover', handleMouse);
        document.addEventListener('mouseleave', handleMouseLeaveDocument);
        document.addEventListener('click', handleMouse);
        window.addEventListener('smindActivateSection', handleForceActive);
        window.addEventListener('smindDeactivateSection', handleForceDeactivate);

        return () => {
            document.removeEventListener('mouseover', handleMouse);
            document.removeEventListener('mouseleave', handleMouseLeaveDocument);
            document.removeEventListener('click', handleMouse);
            window.removeEventListener('smindActivateSection', handleForceActive); // 🌟 Xoá event này
            window.removeEventListener('smindDeactivateSection', handleForceDeactivate);
            clearTimeout(state.hoverTimer);
        };
    }, [onClick]);

    // Các hàm triggerEvent được làm ngắn gọn hơn
    const triggerAction = (actionCallback, ...params) => {
        const sectionId = state?.activeEl?.getAttribute('section-id');
        if (actionCallback && sectionId) actionCallback(sectionId, ...params);
    };

    const triggerPlus = (actionCallback, ...params) => {
        const sectionId = state?.hoverEl?.getAttribute('section-id');
        if (actionCallback && sectionId) actionCallback(sectionId, ...params);
    };

    return (
        <>
            <div className={`smind-outline-section --hover ${isLimited ? 'limited' : ''}`} ref={refs.hoverOut}>
                <OutlineLabel textRef={refs.hoverTxt} />
                <OutlinePlus
                    onAdd={(actionType, direction) => triggerPlus(onAdd, actionType, direction)}
                />
            </div>

            <div className="smind-outline-section --active" ref={refs.activeOut}>
                <OutlineLabel textRef={refs.activeTxt} />
                <OutlineActions
                    onHide={() => { triggerAction(onHide); activateSection(null); }}
                    onRemove={() => { triggerAction(onRemove); activateSection(null); }}
                    onMoveUp={() => triggerAction(onMoveUp)}
                    onMoveDown={() => triggerAction(onMoveDown)}
                />
            </div>
        </>
    );
}

export default OutlineSection;